import { Task } from './task';
import { LocalDate } from 'src/core/common/domain/date/local-date';
import { MasterDataIncoherenceException } from 'src/core/common/domain/exceptions/master-data-incoherence-exception';

export enum ConfirmCloseTaskActionsShow {
  ALWAYS = 'always',
  NEVER = 'never',
  OUTSIDE_VALID_DATE_RANGE = 'outside-valid-date-range',
}

export type ConfirmCloseTaskActionsData = {
  show: ConfirmCloseTaskActionsShow;
  fields: {
    resolutions: {
      show: boolean;
      show_in_bulk: boolean;
      options: Array<{
        id: string;
        name: string;
        isFinal: boolean;
      }>;
    };
  };
};

export class ConfirmCloseTaskActions {
  constructor(public readonly value: ConfirmCloseTaskActionsData) {}

  shouldShowModalForTask(task: Task) {
    if (this.value.show === ConfirmCloseTaskActionsShow.ALWAYS) {
      return true;
    }

    if (this.value.show === ConfirmCloseTaskActionsShow.OUTSIDE_VALID_DATE_RANGE) {
      const today = LocalDate.now();
      return !task.date.range.isInRange(today);
    }

    if (this.value.show === ConfirmCloseTaskActionsShow.NEVER) {
      return false;
    }

    // otherwise
    return false;
  }

  shouldShowResolutions(bulkMode: boolean = false): boolean {
    if (!this.value.fields.resolutions) {
      return false;
    }

    if (bulkMode) {
      return this.value.fields.resolutions.show_in_bulk;
    }

    return this.value.fields.resolutions.show;
  }

  optionsForResolutions(): Array<{ id: string; label: string }> {
    const allOptions = this.value.fields.resolutions.options;

    return allOptions.map((o) => ({
      id: o.id,
      label: o.name,
    }));
  }

  /**
   * The resolution that confirms `task` when no modal asks for one: the only option offered, or the
   * task's own when none is. More options than one need the modal, and no resolution at all leaves
   * nothing to confirm with: master data the app cannot work with either way.
   */
  resolutionToConfirmWithoutModal(task: Task): string {
    const options = this.optionsForResolutions();
    if (options.length > 1) {
      throw new MasterDataIncoherenceException(
        MasterDataIncoherenceException.MORE_THAN_ONE_RESOLUTION_BUT_CLOSE_MODAL_NOT_ENABLED
      );
    }
    if (options.length === 1) {
      return options[0].id;
    }

    const taskResolution = task.resolution?.id ?? null;
    if (taskResolution === null) {
      throw new MasterDataIncoherenceException(
        MasterDataIncoherenceException.NO_RESOLUTIONS_AND_TASK_HAS_NO_RESOLUTION
      );
    }
    return taskResolution;
  }

  isResolutionFinal(resolutionId: string): boolean {
    return this.value.fields.resolutions.options.find((r) => r.id === resolutionId)?.isFinal ?? false;
  }
}
